import { registerLocaleData } from '@angular/common';
import {
	HTTP_INTERCEPTORS,
	provideHttpClient,
	withInterceptorsFromDi
} from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	LOCALE_ID,
	provideAppInitializer,
	provideZoneChangeDetection
} from '@angular/core';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import {
	provideRouter,
	withComponentInputBinding,
	withHashLocation
} from '@angular/router';
import { AppolowDatepickerModule, AppolowLibraryModule } from '@appolow/form';
import {
	AppolowActiveModal,
	AppolowModalConfig,
	AppolowModalModule
} from '@appolow/modal';
import { TableModule } from '@appolow/paginable';
import {
	I18nIntl,
	InjectionService,
	Toast,
	Translations
} from '@core/services';
import { App } from '@shared/interfaces/app';
import { provideTransloco } from '@jsverse/transloco';
import { providePrimeNG } from 'primeng/config';
import { FilterService } from 'primeng/api';
import { SERVER_FILTER_MATCH_MODE } from '@shared/utils/dropdown-pagination';
import { PLATFORM_THEME } from '@shared/constants/platform-theme';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, RootStoreConfig, StoreModule } from '@ngrx/store';
import {
	StoreDevtoolsModule,
	StoreDevtoolsOptions
} from '@ngrx/store-devtools';
import { NgxLoadingModule } from '@shared/components/ngx-loading/ngx-loading.module';
import { provideDefaultConfirmation } from '@shared/services/confirmation.service';
import { appState, loadState } from '@store/app.state';
import { AppEffects } from '@store/effects/app.effects';
import { clearStore } from '@store/reducers/environment.reducer';
import { SortableModule } from '@appolow/sortable';
import { QuillModule } from 'ngx-quill';
import {
	TimeagoCustomFormatter,
	TimeagoFormatter,
	TimeagoIntl,
	TimeagoModule
} from 'ngx-timeago';
import { ToastrModule } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
	AcceptLanguageInterceptor,
	AuthModule,
	AuthService,
	JWTInterceptor
} from './core/auth';
import { NavigationLoadingInterceptor } from './core/auth/interceptors/navigation-loading.interceptor';
import { fieldErrorMessage } from './shared/utils/field-error-message';
import { DialogModule } from './core/modules/dialog/dialog.module';
import { ExtensibilityService } from './core/services/extensibility.service';
import { ChunkLoadErrorHandler } from './core/services/chunk-load-error-handler.service';
import { TranslocoHttpLoader } from './transloco-loader.service';
import { keepColumnFilterOpenOnSelectOverlays } from './core/primeng/column-filter-overlay';
registerLocaleData(localeEn);
registerLocaleData(localeEs);
keepColumnFilterOpenOnSelectOverlays();

export const metaReducers: MetaReducer<any>[] = [loadState, clearStore];

const rootStoreConfig: RootStoreConfig<App[]> = {
	runtimeChecks: {
		strictStateSerializability: true,
		strictActionSerializability: true,
		strictActionWithinNgZone: true,
		strictActionTypeUniqueness: true
	},
	metaReducers: metaReducers
};

const storeDevtoolsOptions: StoreDevtoolsOptions = {
	name: 'NgRx Appolow designer',
	maxAge: 50,
	logOnly: environment.production
};

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		{ provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
		provideDefaultConfirmation(),
		provideRouter(routes, withComponentInputBinding(), withHashLocation()),
		providePrimeNG({
			theme: PLATFORM_THEME,
			overlayAppendTo: 'body',
			zIndex: {
				modal: 1100,
				overlay: 1100,
				menu: 1000,
				tooltip: 1100
			}
		}),
		provideTransloco({
			config: {
				availableLangs: ['en', 'es'],
				defaultLang: Translations.currentLanguage(),
				reRenderOnLangChange: true,
				prodMode: environment.name === 'production',
				missingHandler: {
					logMissingKey: false
				}
			},
			loader: TranslocoHttpLoader
		}),
		{ provide: LOCALE_ID, useValue: 'es-Es' },
		provideAppInitializer(async () => {
			inject(Translations);

			const authSvc = inject(AuthService);
			const extensibility = inject(ExtensibilityService);

			// First: Initialize user authentication
			if (!authSvc.isLoggedIn()) {
				return;
			}

			const user = await firstValueFrom(authSvc.getUser());
			if (!user) {
				return;
			}

			// Initialize extensibility system with user's allowed modules
			await extensibility.initializeForUser(user);
		}),
		provideHttpClient(
			withInterceptorsFromDi() /* , withInterceptors([JWTInterceptor]) */
		),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: JWTInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AcceptLanguageInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: NavigationLoadingInterceptor,
			multi: true
		},
		provideAppInitializer(async () => {
			inject(Toast);
			inject(InjectionService);
			inject(FilterService).register(SERVER_FILTER_MATCH_MODE, () => true);
		}),
		importProvidersFrom(
			SortableModule.forRoot({
				animation: 256
			}),
			NoopAnimationsModule,
			ToastrModule.forRoot({
				positionClass: 'toast-bottom-center',
				preventDuplicates: true
			}),
			AuthModule,
			StoreModule.forRoot(appState, rootStoreConfig),
			EffectsModule.forRoot([AppEffects]),
			StoreDevtoolsModule.instrument(storeDevtoolsOptions),
			DialogModule,
			AppolowLibraryModule.forRoot({
				invalidFeedbackTemplateFn: (key: string, value: string) =>
					fieldErrorMessage(key, value, (k, params) =>
						Translations.instant(k, params)
					)
			}),
			AppolowDatepickerModule,
			TableModule.forRoot(),
			AppolowModalModule,
			NgxLoadingModule.forRoot(),
			QuillModule.forRoot({
				modules: {
					toolbar: [
						['bold', 'italic', 'underline'], // toggled buttons
						['blockquote'],
						[{ list: 'ordered' }, { list: 'bullet' }],
						[{ script: 'sub' }, { script: 'super' }], // superscript/subscript
						[{ indent: '-1' }, { indent: '+1' }], // outdent/indent
						[{ direction: 'rtl' }], // text direction
						[{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
						//   [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
						[{ color: [] }, { background: [] }], // dropdown with defaults from theme
						//   [{ 'font': [] }],
						[{ align: [] }],
						['clean'] // remove formatting button
					]
				}
			}),
			TimeagoModule.forRoot({
				intl: { provide: TimeagoIntl, useClass: I18nIntl },
				formatter: {
					provide: TimeagoFormatter,
					useClass: TimeagoCustomFormatter
				}
			})
		),
		AppolowActiveModal,
		/**
		 * Default @appolow/modal selectors for the platform's `.modal-header` /
		 * `.modal-footer` convention. Without them, v21+ of the library dumps
		 * the whole component template (header + body + footer markup) into a
		 * single `.appolow-modal__body`, breaking scrollable + centered layouts
		 * (dialog overflows the viewport because the middle `.modal-body` is
		 * not a flex item and cannot scroll internally). Individual callers
		 * can still override these when a component uses a different
		 * convention (e.g. `ConfirmDialogComponent`).
		 */
		{
			provide: AppolowModalConfig,
			useFactory: () => {
				const config = new AppolowModalConfig();
				config.headerSelector = '.modal-header';
				config.footerSelector = '.modal-footer';
				return config;
			}
		}
	]
};
